import React, { useCallback, useEffect, useRef, useState, isValidElement, cloneElement } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import { clickToLocationPercent } from "./patrolSectionCameraLocation.js";

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 10;
const ZOOM_STEP = 0.25;
const DRAG_THRESHOLD_PX = 5;

function clampZoom(z) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

/** Contained image size inside a vw×vh box (object-fit: contain). */
function containedSize(vw, vh, iw, ih) {
  if (!iw || !ih) return { width: vw, height: vh };
  const scale = Math.min(vw / iw, vh / ih);
  return { width: iw * scale, height: ih * scale };
}

function measureFitTransform(viewportEl, contentEl) {
  if (!viewportEl || !contentEl) return null;
  const vw = viewportEl.clientWidth;
  const vh = viewportEl.clientHeight;
  if (vw < 1 || vh < 1) return null;

  const img = contentEl.querySelector("img");
  if (img?.naturalWidth > 0 && img?.naturalHeight > 0) {
    const { width: renderW, height: renderH } = containedSize(
      vw,
      vh,
      img.naturalWidth,
      img.naturalHeight,
    );
    const fitZoom = clampZoom(
      Math.min(vw / renderW, vh / renderH) * 0.96,
    );
    return { zoom: fitZoom, pan: { x: 0, y: 0 } };
  }

  const svg = contentEl.querySelector("svg");
  if (svg) {
    return { zoom: 1, pan: { x: 0, y: 0 } };
  }

  return { zoom: 1, pan: { x: 0, y: 0 } };
}

/**
 * @param {{
 *   children: React.ReactNode,
 *   markers?: React.ReactNode,
 *   minHeight?: number,
 *   placementHint?: boolean,
 *   onLocationPick?: (coords: { xPercent: number, yPercent: number }) => void,
 *   borderActive?: boolean,
 *   mediaVersion?: number,
 * }} props
 */
export default function PatrolDrawingZoomViewport({
  children,
  markers = null,
  minHeight = 360,
  placementHint = false,
  onLocationPick,
  borderActive = false,
  mediaVersion = 0,
}) {
  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const dragRef = useRef(null);

  const fitToScreen = useCallback(() => {
    const next = measureFitTransform(viewportRef.current, contentRef.current);
    if (next) {
      setZoom(next.zoom);
      setPan(next.pan);
    }
  }, []);

  useEffect(() => {
    const runFit = () => {
      requestAnimationFrame(() => {
        fitToScreen();
      });
    };
    runFit();
    const t = window.setTimeout(runFit, 150);
    const t2 = window.setTimeout(runFit, 500);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(t2);
    };
  }, [fitToScreen, mediaVersion, minHeight]);

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => fitToScreen());
    ro.observe(vp);
    return () => ro.disconnect();
  }, [fitToScreen]);

  const resetView = useCallback(() => {
    fitToScreen();
  }, [fitToScreen]);

  const zoomIn = useCallback(() => {
    setZoom((z) => clampZoom(Number((z + ZOOM_STEP).toFixed(2))));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((z) => clampZoom(Number((z - ZOOM_STEP).toFixed(2))));
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
    setZoom((z) => clampZoom(Number((z + delta).toFixed(2))));
  }, []);

  const handlePointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      panX: pan.x,
      panY: pan.y,
      moved: false,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, [pan.x, pan.y]);

  const handlePointerMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      drag.moved = true;
      dragRef.current = drag;
      setIsPanning(true);
    }
    setPan({ x: drag.panX + dx, y: drag.panY + dy });
  }, []);

  const finishPointer = useCallback(
    (e) => {
      const drag = dragRef.current;
      dragRef.current = null;
      setIsPanning(false);
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (!drag) return;

      if (!drag.moved && placementHint && onLocationPick && contentRef.current) {
        const coords = clickToLocationPercent(e, contentRef.current);
        onLocationPick(coords);
        return;
      }

      if (drag.moved) {
        setPan({
          x: drag.panX + (e.clientX - drag.startX),
          y: drag.panY + (e.clientY - drag.startY),
        });
      }
    },
    [placementHint, onLocationPick],
  );

  const zoomLabel = `${Math.round(zoom * 100)}%`;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight,
        bgcolor: "#E5E7EB",
        overflow: "hidden",
        border: borderActive ? "2px solid #2932E5" : "1px solid #D1D5DB",
        borderRadius: "12px",
      }}
    >
      <Box
        ref={viewportRef}
        onWheel={handleWheel}
        sx={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          cursor: isPanning ? "grabbing" : placementHint ? "crosshair" : "grab",
        }}
      >
        <Box
          ref={contentRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={finishPointer}
          onPointerCancel={finishPointer}
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: "100%",
            height: "100%",
            minHeight,
            transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px)) scale(${zoom})`,
            transformOrigin: "center center",
            touchAction: "none",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              minHeight,
              bgcolor: "#7C8494",
            }}
          >
            {children}
            {markers && isValidElement(markers)
              ? cloneElement(markers, { zoom })
              : markers}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: 12,
          right: 12,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          bgcolor: "rgba(255,255,255,0.95)",
          borderRadius: "10px",
          border: "1px solid #E5E7EB",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          p: 0.5,
          zIndex: 5,
        }}
      >
        <Tooltip title="Zoom out">
          <span>
            <IconButton size="small" onClick={zoomOut} disabled={zoom <= MIN_ZOOM}>
              <RemoveIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            minWidth: 44,
            textAlign: "center",
            color: "#364153",
          }}
        >
          {zoomLabel}
        </Typography>
        <Tooltip title="Zoom in">
          <span>
            <IconButton size="small" onClick={zoomIn} disabled={zoom >= MAX_ZOOM}>
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Reset zoom and pan">
          <IconButton size="small" onClick={resetView}>
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fit to screen">
          <IconButton size="small" onClick={fitToScreen}>
            <FitScreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

    </Box>
  );
}
